import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang, IMAGES } from "../i18n";
import { Reveal, SectionLabel } from "./Motion";
import { Activity, Boxes, Radio } from "lucide-react";

const ICONS = [Radio, Boxes, Activity];

const Panel = ({ item, index }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [60, -60]);
  const Icon = ICONS[index];
  const reversed = index % 2 === 1;

  return (
    <div ref={ref} className={`relative grid lg:grid-cols-12 gap-8 items-center`}>
      <div className={`lg:col-span-7 ${reversed ? "lg:order-2" : ""}`}>
        <div className="relative group overflow-hidden rounded-[2rem] border border-[var(--border)]">
          <motion.img
            style={{ y }}
            src={IMAGES[item.img]}
            alt={item.t}
            className="w-full h-[380px] md:h-[520px] object-cover scale-110"
          />
          {/* HUD overlays */}
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-primary)]/80 via-transparent to-transparent" />
          <motion.div
            animate={{ top: ["10%", "85%", "10%"] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            className="absolute left-6 right-6 h-px bg-[var(--green)]/60 shadow-[0_0_18px_2px_rgba(22,163,74,0.5)]"
          />
          {["top-5 left-5 border-t border-l", "top-5 right-5 border-t border-r", "bottom-5 left-5 border-b border-l", "bottom-5 right-5 border-b border-r"].map((c, i) => (
            <span key={i} className={`absolute ${c} w-7 h-7 border-[var(--gold)]/50`} />
          ))}
          <div className="absolute top-6 left-6 glass rounded-full px-3 py-1.5 flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--green)] animate-pulse" />
            <span className="font-jetbrains text-[10px] tracking-widest text-[var(--text-primary)]">REAL-TIME</span>
          </div>
        </div>
      </div>

      <div className={`lg:col-span-5 ${reversed ? "lg:order-1 lg:pr-10" : "lg:pl-6"}`}>
        <Reveal>
          <div className="flex items-center gap-3 mb-5">
            <span className="font-jetbrains text-sm text-[var(--gold)]">{String(index + 1).padStart(2, "0")}</span>
            <span className="h-px w-8 bg-[var(--border)]" />
            <Icon className="w-5 h-5 text-[var(--green)]" />
          </div>
          <h3 className="font-cormorant font-light text-3xl md:text-5xl text-[var(--text-primary)] leading-[1.05]">{item.t}</h3>
          <p className="font-outfit font-light text-[var(--text-secondary)] leading-relaxed mt-5 max-w-md">{item.d}</p>
        </Reveal>
      </div>
    </div>
  );
};

export const Showcase = () => {
  const { t } = useLang();
  const s = t.tech;
  return (
    <section id="tech" data-testid="tech-section" className="relative py-20 md:py-40 overflow-hidden">
      {/* futuristic grid backdrop */}
      <div
        className="absolute inset-0 opacity-[0.02] pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(rgba(217,164,65,0.7) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }}
      />
      <div className="absolute top-1/3 right-0 w-[500px] h-[500px] rounded-full bg-[var(--wine)]/15 blur-[150px] pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-8 items-end mb-20">
          <div className="lg:col-span-8">
            <SectionLabel>{s.label}</SectionLabel>
            <h2 className="font-cormorant font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-[var(--text-primary)] max-w-3xl">
              {s.title}
            </h2>
          </div>
          <div className="lg:col-span-4">
            <p className="font-outfit font-light text-[var(--text-secondary)] leading-relaxed">{s.intro}</p>
          </div>
        </div>

        <div className="space-y-20 md:space-y-28">
          {s.items.map((item, i) => (
            <Panel key={i} item={item} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
